import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  ENSURE_COMMAND_DEFINITIONS,
  INITIAL_AUTHENTICATION,
  LOAD_COMMAND_DEFINITIONS,
} from '../../actions/actionTypes';
import {
  loadCommandDefinitions,
  loadCommandDefinitionsFailure,
  loadCommandDefinitionsPending,
  loadCommandDefinitionsSuccess,
} from '../../actions/commandDefinitionActions';
import { Action } from '../../actions/commonActions';
import * as commandDefinitionApi from '../../api/commandDefinitionApi';
import { CommandDefinitionsState } from '../../models/state/commandDefinitionState';
import { CommandTypesPayload, CommandTypesState } from '../../models/state/commandTypeState';
import { selectCommandDefinitions } from '../../selectors/commandDefinitions';

const log = getLogger('simmanagement.sagas.login');

/**
 * Ensure that the command types have been loaded.
 * @param action The action that invoked the saga.
 */
export function* ensureCommandDefinitionsSaga(action: Action<string>) {
  if (!action.payload) {
    return;
  }

  const commandType = action.payload;
  const commandDefinitions: CommandDefinitionsState = yield select(selectCommandDefinitions);
  const commandDefinition = commandDefinitions[commandType];
  if (
    !commandDefinition ||
    ((!commandDefinition.expiresOn || commandDefinition.expiresOn < new Date()) && !commandDefinition.isLoading)
  ) {
    yield put(loadCommandDefinitions(commandType));
  }
}

/**
 * Load the command types.
 * @param action The action that invoked the saga.
 */
export function* loadCommandDefinitionsSaga(action: Action<string>) {
  if (!action.payload) {
    return;
  }

  const commandType = action.payload;
  yield put(loadCommandDefinitionsPending(commandType));
  try {
    const commandDefinitions: CommandTypesPayload = yield call(commandDefinitionApi.getByCommandType, commandType);
    yield put(loadCommandDefinitionsSuccess(commandDefinitions, commandType));
  } catch (error: any) {
    yield put(loadCommandDefinitionsFailure(error, commandType));
  }
}

/*
 * Initialize all sagas related to command types.
 */
export function* initCommandDefinisionsSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_COMMAND_DEFINITIONS, ensureCommandDefinitionsSaga);
  yield takeEvery(LOAD_COMMAND_DEFINITIONS, loadCommandDefinitionsSaga);
}
